/**
 * Target Resolver for AI Presentation Layer Editing
 *
 * Resolves natural language descriptions to specific MMOs on a screen.
 * Handles descriptions like "the title", "the image on the left", etc.
 */

import { FlowObjects } from '../../fsm/types';
import Screens from '../../../model/Screens';
import TextFlowObject from '../../../model/TextFlowObject';
import ShapeFlowObject from '../../../model/ShapeFlowObject';
import ImageFlowObject from '../../../model/ImageFlowObject';
import VideoFlowObject from '../../../model/VideoFlowObject';
import { CommandTarget, ScreenElement, CommandColor } from './CommandSchema';
import { COLOR_MAP, getClosestColorName } from './ColorMap';

/**
 * Result of target resolution
 */
export interface ResolvedTarget {
  /** The resolved MMO or null if not found */
  mmo: FlowObjects | null;
  /** Index of the MMO in the screen's mixedMediaObjects array */
  index: number;
  /** Confidence score (0-1) for the match */
  confidence: number;
  /** Why this target was selected */
  reason: string;
}

/**
 * Resolution context
 */
export interface ResolutionContext {
  screen: Screens;
  screenIndex: number;
  contentMapping?: Record<string, { mmoIds: string[] }>;
}

/**
 * Resolve a CommandTarget to a specific MMO
 */
export function resolveTarget(
  target: CommandTarget,
  context: ResolutionContext
): ResolvedTarget {
  const { screen, contentMapping } = context;
  const mmos = screen.mixedMediaObjects || [];

  // Priority 1: Direct index
  if (typeof target.mmoIndex === 'number') {
    const mmo = mmos[target.mmoIndex];
    if (mmo) {
      return {
        mmo,
        index: target.mmoIndex,
        confidence: 1.0,
        reason: `Direct index: ${target.mmoIndex}`,
      };
    }
    return {
      mmo: null,
      index: -1,
      confidence: 0,
      reason: `Index ${target.mmoIndex} not found (${mmos.length} elements)`,
    };
  }

  // Priority 2: Content ID mapping
  if (target.contentId && contentMapping) {
    const mapping = contentMapping[target.contentId];
    if (mapping && mapping.mmoIds.length > 0) {
      const mmoId = mapping.mmoIds[0];
      const index = mmos.findIndex((m) => m.id === mmoId);
      if (index >= 0) {
        return {
          mmo: mmos[index],
          index,
          confidence: 0.95,
          reason: `Content ID: ${target.contentId}`,
        };
      }
    }
  }

  // Priority 3: Natural language description
  if (target.description) {
    return resolveByDescription(target.description, mmos);
  }

  return {
    mmo: null,
    index: -1,
    confidence: 0,
    reason: 'No valid target identifier provided',
  };
}

/**
 * Resolve target by natural language description
 */
function resolveByDescription(
  description: string,
  mmos: FlowObjects[]
): ResolvedTarget {
  const desc = description.toLowerCase().trim();

  // Type-based resolution
  if (desc.includes('title')) {
    return resolveTitle(mmos);
  }

  if (desc.includes('subtitle') || desc.includes('sub title') || desc.includes('sub-title')) {
    return resolveSubtitle(mmos);
  }

  if (desc.includes('heading') || desc.includes('header')) {
    return resolveHeading(mmos);
  }

  if (desc.includes('body') || desc.includes('paragraph') || desc.includes('text')) {
    return resolveBodyText(mmos, desc);
  }

  if (desc.includes('image') || desc.includes('picture') || desc.includes('photo')) {
    return resolveImage(mmos, desc);
  }

  if (desc.includes('video')) {
    return resolveVideo(mmos, desc);
  }

  if (desc.includes('shape') || desc.includes('rectangle') || desc.includes('box') ||
      desc.includes('circle') || desc.includes('square') || desc.includes('triangle')) {
    return resolveShape(mmos, desc);
  }

  // Position-based resolution
  if (desc.includes('left')) {
    return resolveByPosition(mmos, 'left');
  }

  if (desc.includes('right')) {
    return resolveByPosition(mmos, 'right');
  }

  if (desc.includes('top') || desc.includes('upper')) {
    return resolveByPosition(mmos, 'top');
  }

  if (desc.includes('bottom') || desc.includes('lower')) {
    return resolveByPosition(mmos, 'bottom');
  }

  if (desc.includes('center') || desc.includes('middle')) {
    return resolveByPosition(mmos, 'center');
  }

  // First/last element
  if (desc.includes('first')) {
    if (mmos.length > 0) {
      return {
        mmo: mmos[0],
        index: 0,
        confidence: 0.8,
        reason: 'First element',
      };
    }
  }

  if (desc.includes('last')) {
    if (mmos.length > 0) {
      return {
        mmo: mmos[mmos.length - 1],
        index: mmos.length - 1,
        confidence: 0.8,
        reason: 'Last element',
      };
    }
  }

  // Index from description (e.g., "element 1", "object 2")
  const indexMatch = desc.match(/(?:element|object|item)\s*(\d+)/);
  if (indexMatch) {
    const idx = parseInt(indexMatch[1], 10) - 1; // Convert to 0-indexed
    if (idx >= 0 && idx < mmos.length) {
      return {
        mmo: mmos[idx],
        index: idx,
        confidence: 0.9,
        reason: `Element ${idx + 1}`,
      };
    }
  }

  // Color-based resolution for shapes
  for (const colorName of Object.keys(COLOR_MAP)) {
    if (desc.includes(colorName)) {
      const result = resolveByColor(mmos, colorName);
      if (result.mmo) return result;
    }
  }

  return {
    mmo: null,
    index: -1,
    confidence: 0,
    reason: `Could not resolve: "${description}"`,
  };
}

/**
 * Resolve the title (largest text element, typically at top)
 */
function resolveTitle(mmos: FlowObjects[]): ResolvedTarget {
  const textMmos = mmos
    .map((m, i) => ({ mmo: m, index: i }))
    .filter((item) => item.mmo.type === 'text') as Array<{ mmo: TextFlowObject; index: number }>;

  if (textMmos.length === 0) {
    return { mmo: null, index: -1, confidence: 0, reason: 'No text elements found' };
  }

  // Sort by font size (descending), then by Y position (ascending)
  textMmos.sort((a, b) => {
    const sizeA = a.mmo.font?.fontSize || 0;
    const sizeB = b.mmo.font?.fontSize || 0;
    if (sizeB !== sizeA) return sizeB - sizeA;
    return a.mmo.frame.y - b.mmo.frame.y;
  });

  const title = textMmos[0];
  return {
    mmo: title.mmo,
    index: title.index,
    confidence: 0.9,
    reason: 'Largest text (title)',
  };
}

/**
 * Resolve subtitle (second largest text, or text below title)
 */
function resolveSubtitle(mmos: FlowObjects[]): ResolvedTarget {
  const textMmos = mmos
    .map((m, i) => ({ mmo: m, index: i }))
    .filter((item) => item.mmo.type === 'text') as Array<{ mmo: TextFlowObject; index: number }>;

  if (textMmos.length < 2) {
    return { mmo: null, index: -1, confidence: 0, reason: 'Not enough text elements for subtitle' };
  }

  // Sort by font size (descending)
  textMmos.sort((a, b) => {
    const sizeA = a.mmo.font?.fontSize || 0;
    const sizeB = b.mmo.font?.fontSize || 0;
    return sizeB - sizeA;
  });

  const subtitle = textMmos[1];
  return {
    mmo: subtitle.mmo,
    index: subtitle.index,
    confidence: 0.85,
    reason: 'Second largest text (subtitle)',
  };
}

/**
 * Resolve heading (any prominently sized text)
 */
function resolveHeading(mmos: FlowObjects[]): ResolvedTarget {
  return resolveTitle(mmos); // Treat heading same as title
}

/**
 * Resolve body text (smaller text, not title/subtitle)
 */
function resolveBodyText(mmos: FlowObjects[], desc: string): ResolvedTarget {
  const textMmos = mmos
    .map((m, i) => ({ mmo: m, index: i }))
    .filter((item) => item.mmo.type === 'text') as Array<{ mmo: TextFlowObject; index: number }>;

  if (textMmos.length === 0) {
    return { mmo: null, index: -1, confidence: 0, reason: 'No text elements found' };
  }

  // If looking for specific text position
  if (desc.includes('first text')) {
    return { mmo: textMmos[0].mmo, index: textMmos[0].index, confidence: 0.85, reason: 'First text element' };
  }

  // Sort by font size (ascending) to get body text
  textMmos.sort((a, b) => {
    const sizeA = a.mmo.font?.fontSize || 0;
    const sizeB = b.mmo.font?.fontSize || 0;
    return sizeA - sizeB;
  });

  // Return the smallest text (likely body)
  const body = textMmos[0];
  return {
    mmo: body.mmo,
    index: body.index,
    confidence: 0.7,
    reason: 'Body text (smallest font)',
  };
}

/**
 * Resolve image element
 */
function resolveImage(mmos: FlowObjects[], desc: string): ResolvedTarget {
  const imageMmos = mmos
    .map((m, i) => ({ mmo: m, index: i }))
    .filter((item) => item.mmo.type === 'image');

  if (imageMmos.length === 0) {
    return { mmo: null, index: -1, confidence: 0, reason: 'No image elements found' };
  }

  // If there's only one image, return it
  if (imageMmos.length === 1) {
    return {
      mmo: imageMmos[0].mmo,
      index: imageMmos[0].index,
      confidence: 0.95,
      reason: 'Only image on screen',
    };
  }

  // Position-based selection
  if (desc.includes('left')) {
    imageMmos.sort((a, b) => a.mmo.frame.x - b.mmo.frame.x);
  } else if (desc.includes('right')) {
    imageMmos.sort((a, b) => b.mmo.frame.x - a.mmo.frame.x);
  } else if (desc.includes('top')) {
    imageMmos.sort((a, b) => a.mmo.frame.y - b.mmo.frame.y);
  } else if (desc.includes('bottom')) {
    imageMmos.sort((a, b) => b.mmo.frame.y - a.mmo.frame.y);
  } else if (desc.includes('large') || desc.includes('big') || desc.includes('main')) {
    // Sort by area (descending)
    imageMmos.sort((a, b) => {
      const areaA = a.mmo.frame.width * a.mmo.frame.height;
      const areaB = b.mmo.frame.width * b.mmo.frame.height;
      return areaB - areaA;
    });
  }

  return {
    mmo: imageMmos[0].mmo,
    index: imageMmos[0].index,
    confidence: 0.85,
    reason: `Image (${desc})`,
  };
}

/**
 * Resolve video element
 */
function resolveVideo(mmos: FlowObjects[], desc: string): ResolvedTarget {
  const videoMmos = mmos
    .map((m, i) => ({ mmo: m, index: i }))
    .filter((item) => item.mmo.type === 'video');

  if (videoMmos.length === 0) {
    return { mmo: null, index: -1, confidence: 0, reason: 'No video elements found' };
  }

  return {
    mmo: videoMmos[0].mmo,
    index: videoMmos[0].index,
    confidence: 0.95,
    reason: 'Video element',
  };
}

/**
 * Resolve shape element
 */
function resolveShape(mmos: FlowObjects[], desc: string): ResolvedTarget {
  const shapeMmos = mmos
    .map((m, i) => ({ mmo: m, index: i }))
    .filter((item) => item.mmo.type === 'shape') as Array<{ mmo: ShapeFlowObject; index: number }>;

  if (shapeMmos.length === 0) {
    return { mmo: null, index: -1, confidence: 0, reason: 'No shape elements found' };
  }

  // Filter by shape type if specified
  let filtered = shapeMmos;
  if (desc.includes('rectangle') || desc.includes('box') || desc.includes('square')) {
    filtered = shapeMmos.filter((s) =>
      s.mmo.shapeType === 'rectangle' || s.mmo.shapeType === 'roundedRect'
    );
  } else if (desc.includes('circle')) {
    filtered = shapeMmos.filter((s) => s.mmo.shapeType === 'circle');
  } else if (desc.includes('triangle')) {
    filtered = shapeMmos.filter((s) => s.mmo.shapeType === 'triangle');
  } else if (desc.includes('arrow')) {
    filtered = shapeMmos.filter((s) => s.mmo.shapeType === 'arrow');
  }

  if (filtered.length === 0) {
    filtered = shapeMmos; // Fall back to all shapes
  }

  return {
    mmo: filtered[0].mmo,
    index: filtered[0].index,
    confidence: 0.85,
    reason: `Shape (${desc})`,
  };
}

/**
 * Resolve by position on screen
 */
function resolveByPosition(mmos: FlowObjects[], position: 'left' | 'right' | 'top' | 'bottom' | 'center'): ResolvedTarget {
  if (mmos.length === 0) {
    return { mmo: null, index: -1, confidence: 0, reason: 'No elements on screen' };
  }

  const indexed = mmos.map((m, i) => ({ mmo: m, index: i }));

  switch (position) {
    case 'left':
      indexed.sort((a, b) => a.mmo.frame.x - b.mmo.frame.x);
      break;
    case 'right':
      indexed.sort((a, b) => b.mmo.frame.x - a.mmo.frame.x);
      break;
    case 'top':
      indexed.sort((a, b) => a.mmo.frame.y - b.mmo.frame.y);
      break;
    case 'bottom':
      indexed.sort((a, b) => b.mmo.frame.y - a.mmo.frame.y);
      break;
    case 'center': {
      // Find element closest to center
      const centerX = 512; // Half of 1024
      const centerY = 374; // Half of 748
      indexed.sort((a, b) => {
        const distA = Math.abs(a.mmo.frame.x + a.mmo.frame.width / 2 - centerX) +
                      Math.abs(a.mmo.frame.y + a.mmo.frame.height / 2 - centerY);
        const distB = Math.abs(b.mmo.frame.x + b.mmo.frame.width / 2 - centerX) +
                      Math.abs(b.mmo.frame.y + b.mmo.frame.height / 2 - centerY);
        return distA - distB;
      });
      break;
    }
  }

  return {
    mmo: indexed[0].mmo,
    index: indexed[0].index,
    confidence: 0.75,
    reason: `${position} element`,
  };
}

/**
 * Resolve shape by color
 */
function resolveByColor(mmos: FlowObjects[], colorName: string): ResolvedTarget {
  const targetColor = COLOR_MAP[colorName];
  if (!targetColor) {
    return { mmo: null, index: -1, confidence: 0, reason: `Unknown color: ${colorName}` };
  }

  const shapeMmos = mmos
    .map((m, i) => ({ mmo: m, index: i }))
    .filter((item) => item.mmo.type === 'shape') as Array<{ mmo: ShapeFlowObject; index: number }>;

  for (const shape of shapeMmos) {
    const shapeColor = shape.mmo.color;
    if (shapeColor) {
      // Check if colors are similar
      const colorMatch = getClosestColorName({
        r: shapeColor.r,
        g: shapeColor.g,
        b: shapeColor.b,
        a: shapeColor.a,
      });
      if (colorMatch === colorName) {
        return {
          mmo: shape.mmo,
          index: shape.index,
          confidence: 0.9,
          reason: `${colorName} shape`,
        };
      }
    }
  }

  return { mmo: null, index: -1, confidence: 0, reason: `No ${colorName} shape found` };
}

/**
 * Build screen context for AI command generation
 */
export function buildScreenContext(
  screen: Screens,
  screenIndex: number,
  contentMapping?: Record<string, { screenIndex: number; mmoIds: string[] }>,
  canvasSize?: { width: number; height: number }
): {
  screenIndex: number;
  screenSize: { width: number; height: number };
  background: CommandColor;
  elements: ScreenElement[];
} {
  const screenSize = canvasSize || { width: 1024, height: 748 };
  const mmos = screen.mixedMediaObjects || [];
  const bgColor = screen.FlowScreenModelBackgroundColorKey;

  const elements: ScreenElement[] = mmos.map((mmo, index) => {
    const element: ScreenElement = {
      index,
      type: mmo.type as ScreenElement['type'],
      frame: {
        x: mmo.frame.x,
        y: mmo.frame.y,
        width: mmo.frame.width,
        height: mmo.frame.height,
      },
    };

    // Find content ID if available
    if (contentMapping) {
      for (const [contentId, mapping] of Object.entries(contentMapping)) {
        if (mapping.mmoIds.includes(mmo.id)) {
          element.contentId = contentId;
          break;
        }
      }
    }

    // Add type-specific properties
    if (mmo.type === 'text') {
      const textMmo = mmo as TextFlowObject;
      // Truncate text to keep prompt size manageable
      const fullText = textMmo.text || '';
      element.text = fullText.length > 300 ? fullText.substring(0, 300) + '...' : fullText;
      element.fontSize = textMmo.font?.fontSize;
      element.fontName = textMmo.font?.fontName;
      element.textColor = textMmo.textColor ? {
        r: textMmo.textColor.r,
        g: textMmo.textColor.g,
        b: textMmo.textColor.b,
        a: textMmo.textColor.a,
      } : undefined;

      // Generate description
      const fontSize = textMmo.font?.fontSize || 18;
      if (fontSize >= 40) {
        element.description = 'title text';
      } else if (fontSize >= 28) {
        element.description = 'subtitle text';
      } else {
        element.description = 'body text';
      }
    } else if (mmo.type === 'shape') {
      const shapeMmo = mmo as ShapeFlowObject;
      element.shapeType = shapeMmo.shapeType;
      element.color = shapeMmo.color ? {
        r: shapeMmo.color.r,
        g: shapeMmo.color.g,
        b: shapeMmo.color.b,
        a: shapeMmo.color.a,
      } : undefined;

      const colorName = element.color ? getClosestColorName(element.color) : '';
      element.description = `${colorName} ${shapeMmo.shapeType}`;
    } else if (mmo.type === 'image') {
      element.description = 'image';
    } else if (mmo.type === 'video') {
      element.description = 'video';
    }

    return element;
  });

  return {
    screenIndex,
    screenSize,
    background: bgColor ? {
      r: bgColor.r,
      g: bgColor.g,
      b: bgColor.b,
      a: bgColor.a,
    } : { r: 255, g: 255, b: 255, a: 1 },
    elements,
  };
}
